if (!customElements.get('minha-moto')) {
  const LEVELS = ['marca', 'modelo', 'ano'];
  // Marca and modelo are required; ano only narrows the motos when chosen.
  const REQUIRED = 2;
  const FILTER = 'filter.p.m.custom.motos';
  const collator = new Intl.Collator('pt-BR', { numeric: true });
  // Same slug as cli.slug in anuncios-1k/_scripts/loja: the brand collection handle is "moto-<slug>".
  const handleize = (text) =>
    text
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  customElements.define(
    'minha-moto',
    class MinhaMoto extends HTMLElement {
      connectedCallback() {
        this.selects = [...this.querySelectorAll('select[data-level]')];
        this.tipo = this.querySelector('select[data-tipo]');
        this.button = this.querySelector('button[type="submit"]');
        this.status = this.querySelector('[role="status"]');

        // Página do produto: se o cliente já escolheu na barra uma moto em que este produto serve, os campos mostram a
        // dele (assets/moto-cliente.js); senão ficam com a moto do produto, vinda do Liquid.
        const cliente =
          this.dataset.motosProduto && window.MotoCliente?.noProduto(JSON.parse(this.dataset.motosProduto));
        if (cliente?.marca && cliente.modelo) {
          [cliente.marca, cliente.modelo, cliente.ano].forEach((valor, indice) => {
            const select = this.selects[indice];
            if (!valor) return;
            if (![...select.options].some((opcao) => opcao.value === valor)) select.add(new Option(valor, valor));
            select.value = valor;
            select.disabled = false;
          });
        }

        // The motos and the parts in stock (about 26 pages of JSON) are only downloaded when the customer shows interest in the bar,
        // or right away on a results page of the bar, to show the choices that led there.
        const start = () => this.start();
        ['pointerenter', 'focusin', 'touchstart'].forEach((type) =>
          this.addEventListener(type, start, { passive: true })
        );

        // Phone: the button above the fields opens and closes them.
        const band = this.closest('.mm');
        const opener = band?.querySelector('[data-mm-abrir]');
        // Results page of the bar: the fields show the choices that led there, open on phones too.
        if (new URLSearchParams(location.search).has(FILTER)) {
          this.lembrar();
          this.start();
          opener?.setAttribute('aria-expanded', 'true');
          band?.classList.add('mm--aberta');
        }
        // The sidebar filters (assets/facets.js) change the URL without a reload.
        document.addEventListener('facets:url', () => {
          this.lembrar();
          if (this.motos) this.fill(0, this.chosenInUrl());
        });
        opener?.addEventListener('click', () => {
          const open = opener.getAttribute('aria-expanded') !== 'true';
          opener.setAttribute('aria-expanded', String(open));
          band.classList.toggle('mm--aberta', open);
          if (open) {
            this.start();
            this.selects[0].focus();
          }
        });
        // "Mostrar os campos já abertos no celular": only the phone layout shows the fields open from the start.
        if (opener?.getAttribute('aria-expanded') === 'true' && !window.matchMedia('(min-width: 990px)').matches) {
          this.start();
        }

        // A list opened before the motos arrive would only show "Selecione" (and the product's moto, on a product
        // page): wait for them, then open it, unless the customer has moved on meanwhile (another click, a key, a scroll).
        this.selects.forEach((select) =>
          select.addEventListener('mousedown', (event) => {
            if (this.motos) return;
            event.preventDefault();
            const pedido = {};
            this.listaPendente = pedido;
            const cancelar = () => {
              if (this.listaPendente === pedido) this.listaPendente = null;
            };
            ['pointerdown', 'keydown', 'wheel', 'touchmove', 'scroll'].forEach((type) =>
              window.addEventListener(type, cancelar, { once: true, capture: true, passive: true })
            );
            this.start().then(() => {
              if (this.listaPendente !== pedido || !this.motos || select.disabled) return;
              this.listaPendente = null;
              const ativo = document.activeElement;
              if (ativo && ativo !== document.body && !this.contains(ativo)) return;
              select.focus({ preventScroll: true });
              try {
                select.showPicker();
              } catch (error) {
                // showPicker needs a recent click in some browsers; the focused select is enough then.
              }
            });
          })
        );

        // A choice made before the motos arrive is kept by the load, which fills the fields from what they show.
        this.selects.forEach((select, index) =>
          select.addEventListener('change', () => this.motos && this.fill(index + 1))
        );
        // The selects have no name: the search sends one filter value per moto that fits the choices.
        // Shopify drops filters on collections over 5,000 products, so it goes to the brand's collection
        // (kept by anuncios-1k/_scripts/loja/colecoes_moto.py).
        this.button.form.addEventListener('submit', async (event) => {
          event.preventDefault();
          // On a product page the fields start filled, so the button works before the motos arrive.
          await this.start();
          if (!this.motos || this.button.disabled) return;
          const params = new URLSearchParams();
          const ids = this.matching().map((moto) => moto.id);
          ids.forEach((id) => params.append(FILTER, id));
          if (this.tipo.value) params.append('filter.p.product_type', this.tipo.value);
          const colecao = `${this.dataset.collectionsUrl}/moto-${handleize(this.selects[0].value)}`;
          const [marca, modelo, ano] = this.selects.map((select) => select.value);
          window.MotoCliente?.guardar(ids, colecao, { marca, modelo, ano });
          location.assign(`${colecao}?${params}`);
        });
      }

      start() {
        if (!this.loading) this.loading = this.load();
        return this.loading;
      }

      // Página de resultado da barra (/collections/moto-<marca>?filter...): as motos do filtro são a moto do cliente,
      // guardada para as páginas de produto (assets/moto-cliente.js). O nome (marca, modelo, ano) vem quando as motos
      // carregam (load).
      lembrar(nome) {
        const params = new URLSearchParams(location.search);
        // "origem=mais-para": as motos misturadas de um produto (assets/mais-para-moto.js), não a moto do cliente.
        if (params.get('origem') === 'mais-para') return;
        const ids = params.getAll(FILTER);
        if (ids.length && /\/collections\/moto-[^/]+\/?$/.test(location.pathname)) {
          window.MotoCliente?.guardar(ids, location.pathname, nome);
        }
      }

      // Every page of a data section (Section Rendering API).
      async fetchSection(section) {
        const fetchPage = async (query = '') => {
          const response = await fetch(`${this.dataset.url}?section_id=${section}${query}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const script = html.querySelector('script[type="application/json"]');
          return { ...script.dataset, ...JSON.parse(script.textContent) };
        };
        const first = await fetchPage();
        const rest = await Promise.all(
          Array.from({ length: Number(first.pages) - 1 }, (_, index) => fetchPage(`&${first.pageParam}=${index + 2}`))
        );
        return [first, ...rest];
      }

      async load() {
        // Loading starts on hover, so the message only shows when the wait is noticeable.
        const slow = setTimeout(() => {
          if (!this.motos) this.status.textContent = 'Carregando motos…';
        }, 600);
        try {
          const [motos, estoque] = await Promise.all(
            ['minha-moto-data', 'minha-moto-estoque'].map((section) => this.fetchSection(section))
          );
          // Part types of each moto, from the products in stock. Shopify's filter only sees the first 100 motos
          // of a product, so the rest would lead to an empty page.
          const tipos = new Map();
          estoque.flatMap((page) => page.produtos).forEach((produto) => {
            if (!produto) return;
            const [tipo, ids] = produto;
            ids.slice(0, 100).forEach((id) => {
              if (!tipos.has(id)) tipos.set(id, new Set());
              tipos.get(id).add(tipo);
            });
          });
          // A moto with no part in stock is left out: it would lead to an empty page.
          const numero = (moto) => moto.id.slice(moto.id.lastIndexOf('/') + 1);
          this.motos = motos.flatMap((page) => page.motos).filter((moto) => tipos.has(numero(moto)));
          this.motos.forEach((moto) => (moto.tipos = [...tipos.get(numero(moto))]));
          // The modelo carries the cilindrada: most names have it ("Cg 125 Titan", "Cb 300r"), the rest get it
          // ("Mt-03 321"). sections/minha-moto.liquid labels a product page's moto the same way.
          this.motos.forEach((moto) => {
            if (!moto.modelo.includes(String(moto.cilindrada))) moto.modelo += ` ${moto.cilindrada}`;
          });
          this.tipo.disabled = false;
          this.status.textContent = this.motos.length ? '' : 'Nenhuma moto cadastrada ainda.';
          this.fill(0, this.chosenInUrl());
          if (new URLSearchParams(location.search).has(FILTER)) {
            const [marca, modelo, ano] = this.chosenInUrl();
            if (marca && modelo) this.lembrar({ marca, modelo, ano });
          }
        } catch (error) {
          console.error(error);
          this.loading = null;
          this.status.textContent = 'Não foi possível carregar as motos. Tente de novo em instantes.';
        } finally {
          clearTimeout(slow);
        }
      }

      // On a results page of the bar, the choices that led there, as [...LEVELS, tipo]. A field matching
      // several values (motos of several years, types ticked in the sidebar) stays empty.
      chosenInUrl() {
        const params = new URLSearchParams(location.search);
        const ids = params.getAll(FILTER);
        const motos = this.motos.filter((moto) => ids.includes(moto.id));
        if (!motos.length) return [];
        const one = (values) => (new Set(values).size === 1 ? values[0] : '');
        return [
          ...LEVELS.map((key) => one(motos.map((moto) => String(moto[key])))),
          one(params.getAll('filter.p.product_type')),
        ];
      }

      // Motos that fit every choice made above `level`; an empty field fits any value.
      matching(level = LEVELS.length) {
        const chosen = this.selects.slice(0, level).map((select) => select.value);
        return this.motos.filter((moto) =>
          chosen.every((value, index) => !value || String(moto[LEVELS[index]]) === value)
        );
      }

      // Rebuilds the selects from `level` down and the part types, keeping each choice (or the `wanted` one)
      // that still fits the choices above it. Ano opens once marca and modelo are chosen.
      fill(level, wanted = []) {
        this.selects.slice(level).forEach((select, offset) => {
          const index = level + offset;
          const current = wanted[index] ?? select.value;
          select.length = 1;
          select.disabled = !this.selects.slice(0, Math.min(index, REQUIRED)).every((parent) => parent.value);
          if (select.disabled) return;
          const labels = [...new Set(this.matching(index).map((moto) => String(moto[LEVELS[index]])))].sort(
            collator.compare
          );
          if (LEVELS[index] === 'ano') labels.reverse();
          labels.forEach((label) => select.add(new Option(label, label, false, label === current)));
        });

        // Only types with parts in stock for the motos that fit (every moto before a marca is chosen).
        const marca = this.selects[0].value;
        const tipos = new Set(this.matching().flatMap((moto) => moto.tipos));
        const tipo = wanted[LEVELS.length] ?? this.tipo.value;
        this.tipo.length = 1;
        [...tipos]
          .sort(collator.compare)
          .forEach((label) => this.tipo.add(new Option(label, label, false, label === tipo)));
        this.button.disabled = !(marca && this.selects[1].value);
      }
    }
  );
}
